import { useEffect } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';

import { GetPackagesResult } from 'api/hooks/public/useGetPackages';
import { usePackageRegistration } from 'api/hooks/public/usePackageRegistration';
import { useDialogs } from '@toolpad/core/useDialogs';

import FeedbackDialog from 'components/Dialogs/FeedbackDialog';

import { getApiErrorMessage } from 'utils/getApiErrorMessage';

import RegistrationFormStepper from './RegistrationFormStepper';
import {
  RegistrationFormInputValues,
  RegistrationFormOutputValues,
  registrationFormSchema,
} from './utils/validationSchema';

interface MultiStepRegistrationFormProps {
  package: GetPackagesResult;
}

function MultiStepRegistrationForm(props: MultiStepRegistrationFormProps) {
  const dialogs = useDialogs();
  const navigate = useNavigate();

  const { mutateAsync, isPending } = usePackageRegistration({
    async onSuccess() {
      await dialogs.open(FeedbackDialog, {
        icon: 'success',
        title: 'You have been registered!',
        message: "Almost done! We've sent verification emails to the student and parent email addresses you provided. Please click the link in the email to verify your account before logging in.",
        okText: 'Login To Your Account',
      });
      navigate('/login');
    },
    onError(error) {
      console.log(error);
      dialogs.open(FeedbackDialog, {
        title: 'Error',
        message: getApiErrorMessage(error),
      });
    },
  });

  const form = useForm<RegistrationFormInputValues, unknown, RegistrationFormOutputValues>({
    resolver: zodResolver(registrationFormSchema),
    mode: 'onBlur',
    defaultValues: {
      package_id: props.package.id,
      // courses: props.package?.courses?.map((course) => ({
      //   course_id: course?.id,
      //   section_id: course?.sections?.[0]?.id
      // })),
      password: '',
      user_phones: [
        {
          priority: 1,
          type: undefined,
          phone: '',
          extension: '',
        },
      ],
      user_addresses: [
        {
          priority: 1,
          street_address: '',
          city: '',
          state: '',
          zip: '',
        },
      ],
    },
  });

  const { handleSubmit, setValue } = form;

  useEffect(() => {
    if (props.package) {
      setValue(
        'courses',
        props.package?.courses?.map((course) => {
          if (course?.sections?.length === 1) {
            return {
              course_id: course?.id,
              section_id: course?.sections[0]?.id ?? undefined,
            };
          }
          return {
            course_id: course?.id,
            section_id: null,
          };
        }),
      );
    }
  }, [props.package, setValue]);

  const onSubmit = (values: RegistrationFormOutputValues) => {
    console.log(JSON.stringify(values, null, 2));
    if (!values.package_id) {
      alert('Unable to submit form. Unknown package id.');
      return;
    }

    mutateAsync({
      id: props.package.id,
      form: values,
    });

    return values;
  };

  if (!props.package) {
    return (
      <Box display="flex" justifyContent="center">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <RegistrationFormStepper package={props.package} isSubmitting={isPending} />
      </form>
    </FormProvider>
  );
}

export default MultiStepRegistrationForm;
